import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetGroupByIdQuery, useUpdateGroupMutation } from "../../redux/admin/groupApi";
import { toast } from "react-hot-toast";

import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Skeleton,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { ArrowLeft, Save, Palette, Folder } from "lucide-react";

const PRESET_COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
  "#EC4899", "#06B6D4", "#F97316", "#6366F1", "#14B8A6",
];

export default function EditGroup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));

  const { data, isLoading, error, refetch } = useGetGroupByIdQuery(id);
  const [updateGroup, { isLoading: isSaving }] = useUpdateGroupMutation();

  const group = data?.data;

  const [form, setForm] = useState({
    name: "",
    description: "",
    color: "#3b82f6",
  });

  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (group) {
      setForm({
        name: group.name || "",
        description: group.description || "",
        color: group.color || "#3b82f6",
      });
    }
  }, [group]);

  const validate = () => {
    const e = {};
    const name = form.name.trim();
    if (!name) e.name = "Group name is required";
    else if (name.length < 2) e.name = "Name must be at least 2 characters";
    else if (name.length > 100) e.name = "Name cannot exceed 100 characters";

    if (form.description?.length > 500) e.description = "Max 500 characters";

    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    try {
      await updateGroup({
        id,
        name: form.name.trim(),
        description: form.description.trim(),
        color: form.color,
      }).unwrap();
      toast.success("Group updated");
      navigate(`/groups/${id}`);
    } catch (err) {
      toast.error(err?.data?.msg || "Update failed");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}
        >
          <Stack spacing={2}>
            <Skeleton width="40%" height={34} />
            <Skeleton variant="rounded" height={140} />
            <Skeleton variant="rounded" height={200} />
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}
        >
          <Stack spacing={1.2}>
            <Typography fontWeight={900} color="error">
              {error?.data?.msg || "Error loading group"}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => navigate("/groups")} sx={{ borderRadius: 2 }}>
                Back
              </Button>
              <Button variant="contained" onClick={() => refetch()} sx={{ borderRadius: 2 }}>
                Retry
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
      >
        <Stack spacing={0.6}>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate(`/groups/${id}`)}
            sx={{ borderRadius: 2, width: "fit-content" }}
            disabled={isSaving}
          >
            Back to Detail
          </Button>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="h5" fontWeight={900}>
              ✏️ Edit Group
            </Typography>
            <Chip
              icon={<Folder size={16} />}
              label={group.groupCode || "GROUP"}
              variant="outlined"
              sx={{ borderRadius: 2, fontWeight: 800 }}
            />
          </Stack>

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Update basic details and group color.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/groups/${id}`)}
            disabled={isSaving}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={submit}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={18} sx={{ color: "white" }} /> : <Save size={18} />}
            sx={{ borderRadius: 2, fontWeight: 900, px: 2.2 }}
          >
            {isSaving ? "Saving…" : "Save Changes"}
          </Button>
        </Stack>
      </Stack>

      {/* Form */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems="stretch">
          {/* Left */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2}>
              <TextField
                label="Group Name *"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name || " "}
                fullWidth
                size="small"
                inputProps={{ maxLength: 100 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                error={Boolean(fieldErrors.description)}
                helperText={
                  fieldErrors.description ? fieldErrors.description : `${(form.description || "").length}/500`
                }
                fullWidth
                size="small"
                multiline
                rows={4}
                inputProps={{ maxLength: 500 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Stack>
          </Box>

          {/* Right */}
          <Box sx={{ width: { xs: "100%", md: 360 } }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                background: alpha(form.color, 0.06),
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Palette size={18} />
                <Typography fontWeight={900}>Group Color</Typography>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    background: form.color,
                    display: "grid",
                    placeItems: "center",
                    color: "white",
                    flexShrink: 0,
                  }}
                >
                  <Folder size={18} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={900} noWrap>
                    {form.name?.trim() || "Preview"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                    {form.color}
                  </Typography>
                </Box>

                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                  style={{
                    width: 38,
                    height: 38,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                  title="Custom color"
                />
              </Paper>

              <Divider />

              <Stack direction="row" gap={1} flexWrap="wrap">
                {PRESET_COLORS.map((c) => {
                  const selected = form.color === c;
                  return (
                    <Tooltip key={c} title={c}>
                      <IconButton
                        onClick={() => setForm((p) => ({ ...p, color: c }))}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 2.2,
                          background: c,
                          border: selected ? `3px solid ${alpha("#0f172a", 0.85)}` : `2px solid transparent`,
                          "&:hover": { transform: "scale(1.06)" },
                          transition: "0.15s ease",
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>

              {!isDownMd && (
                <Button
                  onClick={submit}
                  variant="contained"
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={18} sx={{ color: "white" }} /> : <Save size={18} />}
                  sx={{ mt: "auto", borderRadius: 2, fontWeight: 900, py: 1.1 }}
                >
                  {isSaving ? "Saving…" : "Save Changes"}
                </Button>
              )}
            </Paper>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
